import React, { useState, useMemo, useCallback, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import GraphContainer from './connector/GraphContainer';
import CustomDatePicker from './components/DatePicker';
import FetchData from './connector/FetchData';
import * as d3 from 'd3';
import { useGesture } from '@use-gesture/react';
import HoverInfo from './components/HoverInfo';
import Toggel from './components/Toggle';
import MultiLine from './svg/MultiLine';
import Bar from './svg/Bar';
import { DST_G_SCALE_LEVELS } from './utils/dstGScale';
import { KP_G_SCALE_LEVELS } from './utils/kpGScale';
import { XRAY_FLARE_CLASS_LEVELS, XRAY_R_SCALE_LEVELS } from './utils/xrayFlareScale';

function App() {
  // State for data and date ranges
  const [dstData, setDstData] = useState([]);
  const [bzData, setBzData] = useState([]);
  const [GNNProtData, setGNNProtData] = useState([]);
  const [KpData, setKpData] = useState([]);
  const [XrayData, setXrayData] = useState([]);

  //State for toggle visiblity
  const [isDstVisible, setIsDstVisible] = useState(true);
  const [isSolarWindVisible, setIsSolarWindVisible] = useState(true);
  const [isGNNProtVisible, setIsGNNProtVisible] = useState(false);
  const [isKpVisible, setIsKpVisible] = useState(true);
  const [isXrayVisible, setIsXrayVisible] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const visibleGraphCount = [
    isXrayVisible,
    isSolarWindVisible,
    isGNNProtVisible,
    isDstVisible,
    isKpVisible,
  ].filter(Boolean).length;

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
    return () => cancelAnimationFrame(id);
  }, [isSidebarOpen, visibleGraphCount]);

  
  // Set default dates to the most recent hour and two weeks before
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    now.setMinutes(0, 0, 0); // Set to the most recent hour
    now.setDate(now.getDate() + 2); // Add 2 days to the current date
    return now;
  });

  const [startDate, setStartDate] = useState(() => {
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setMinutes(0, 0, 0); // Set to the most recent hour
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14); // Subtract 14 days
    return twoWeeksAgo;
  });

  // State for zoom and timeline shift
  const [slider2Value, setSlider2Value] = useState(20); // Time zoom slider (Days to 1 Year)
  const [dragShift, setDragShift] = useState(0);

  const [zoomTransform, setZoomTransform] = useState(d3.zoomIdentity.scale(0.8).translate(50, 0)); // Shared zoom transform with initial zoom out

  // Data processing functions
  const processDSTData = useCallback((line) => ({
    timekey: line.time_key,
    dst_value: line.dst_value ?? 0, // Use dst_value, fallback to Dst, or default to 0
    Dst: line.Dst ?? 0, // Default to 0 if Dst is null
    date: d3.timeParse('%Y%m%d%H%M')(line.time_key), // Parse time_key into a Date object
  }), []);
 

  const processBZData = useCallback((line) => ({
    timekey: line.time_key,
    Bz: line.bz_gsm,
    date: d3.timeParse('%Y%m%d%H%M%S')(line.time_key), // Parse time_key into a Date object
  }), []);

  const processGNNProtData = useCallback((line) => ({
    timekey: line.time_key,
    P10: line.P10 > 0 ? line.P10 : null,
    P8: line.P8A > 0 ? line.P8A : null,
    P5: line.P5 > 0 ? line.P5 : null,
    P4: line.P4 > 0 ? line.P4 : null,
    date: d3.timeParse('%Y%m%d%H%M')(line.time_key),
  }), []);
  

  const processKpData = useCallback((line) => ({
    timekey: line.time_key,
    Kp: line.kp,
    date: d3.timeParse('%Y%m%d%H%M')(line.time_key), // Parse time_key into a Date object
  }), []);

  const processXrayData = useCallback((line) => ({
    timekey: line.time_key,
    comp_flux: line.comp_flux,
    date : d3.timeParse('%Y%m%d%H%M')(line.time_key), // Parse time_key into a Date object
  }), []);


  // Calculate Y-axis domain
  const calculateYDomain = useCallback((min, max) => [min, max], []);

  // Gesture bindings for zooming and shifting the timeline
  const bindGraphGesture = useGesture({
    onPinch: ({ offset: [d], event }) => {
      event.preventDefault(); // Prevent page zooming
      setSlider2Value((prev) => Math.min(Math.max(prev - d / 10, 1), 365)); // Adjust time zoom (1 to 365 days)
    },
    onWheel: ({ delta: [, dy], event }) => {
      event.preventDefault(); // Prevent page scrolling
      setSlider2Value((prev) => Math.min(Math.max(prev + dy / 100, 1), 365)); // Adjust time zoom using trackpad
    },
  });

  // Handle timeline shift from drag events
  const handleDrag = useCallback((timeDelta) => {
    setDragShift((prevShift) => prevShift + timeDelta); // Update shared drag shift in milliseconds
  }, []);

  //handle zooming 
  const handleZoom = useCallback((transform) => {
    setZoomTransform(transform); // Update the shared zoom transform
  }, []);

    // State for hover information
    const [hoverInfo, setHoverInfo] = useState({ date: null, time: null, value: null });

    // Function to update hover info (to be passed to GraphContainer)
    const handleHover = useCallback((hoverData) => {
      if (hoverData && hoverData.date instanceof Date) {
        setHoverInfo({
          date: hoverData.date.toLocaleDateString(),
          time: hoverData.time ? new Date(hoverData.time).toLocaleTimeString() : hoverData.date.toLocaleTimeString(),
          value: hoverData.value,
        });
      } else {
        setHoverInfo({ date: null, time: null, value: null });
      }
    }, []);

  // Memoize props for the Kyoto DST Graph
  const dstGraphProps = useMemo(() => ({
    route: "/api/data/dst",
    setData: setDstData,
    data: dstData,
    title: "Kyoto Dst",
    xAxisLabel: "Date",
    yAxisLabel: "nT",
    yDomain: calculateYDomain(-400, 100),
    yValueAccessor: (d) => d.Dst,
    lineColor: "steelblue",
    timeKeyField: "time_key",
    timeFormat: "%Y%m%d%H%M",
    processData: processDSTData,
    startDate: startDate ? startDate.toISOString() : null,
    endDate: endDate ? endDate.toISOString() : null,
    drag:handleDrag,
    zoomTransform,
    onZoom: handleZoom,
    bindGraphGesture,
    dragShift,
    onHover: handleHover
  }), [
    setDstData,
    dstData,
    calculateYDomain,
    processDSTData,
    startDate,
    endDate,
    handleDrag,
    zoomTransform,
    handleZoom,
    bindGraphGesture,
    dragShift,
    handleHover
  ]);

  // Memoize props for the Bz Solar Wind Graph
  const BzGraphProps = useMemo(() => ({
    route: "/api/data/bz",
    setData: setBzData,
    data: bzData,
    title: "SOLAR-1 wind Bz",
    xAxisLabel: "Date",
    yAxisLabel: "nT",
    xDomain: [startDate, endDate],
    yDomain: calculateYDomain(-20, 20),
    yValueAccessor: (d) => d.Bz,
    lineColor: "#1f77b4",
    timeKeyField: "time_key",
    timeFormat: "%Y%m%d%H%M%S",
    processData: processBZData,
    startDate: startDate ? startDate.toISOString() : null,
    endDate: endDate ? endDate.toISOString() : null,
    drag:handleDrag,
    zoomTransform,
    onZoom: handleZoom,
    bindGraphGesture,
    dragShift,
    onHover: handleHover
  }), [
    setBzData,
    bzData,
    calculateYDomain,
    processBZData,
    startDate,
    endDate,
    handleDrag,
    zoomTransform,
    handleZoom,
    bindGraphGesture,
    dragShift,
    handleHover
  ]);

  // Memoize props for the Solar Wind MultiLine Graph (GNN Proton P2A and P4)
  const GNNGraphProps = useMemo(() => ({
    route: "/api/data/GNNProt",
    setData: setGNNProtData,
    data: GNNProtData,
    title: "GOES Proton Flux",
    xAxisLabel: "Date",
    yAxisLabel: "#cm^2/sec",
    yDomain: calculateYDomain(1e-2, 1e4),
    timeKeyField: "time_key",
    timeFormat: "%Y%m%d%H%M",
    processData: processGNNProtData,
    startDate: startDate ? startDate.toISOString() : null,
    endDate: endDate ? endDate.toISOString() : null,
    dragShift,
    zoomTransform,
    onZoom: handleZoom,
    bindGraphGesture,
    onHover: handleHover
  }), [
    setGNNProtData,
    GNNProtData,
    calculateYDomain,
    processGNNProtData,
    startDate,
    endDate,
    dragShift,
    zoomTransform,
    handleZoom,
    bindGraphGesture,
    handleHover
  ]);
  // Memoize props for the Bz Solar Wind Graph
  const KpGraphProps = useMemo(() => ({
    route: "/api/data/Kp",
    setData: setKpData,
    data: KpData,
    title: "Kp Index",
    xAxisLabel: "Date",
    yAxisLabel: "Kp Index",
    yDomain: calculateYDomain(0, 10),
    yValueAccessor: (d) => d.Kp,
    barColor: "steelblue",
    timeKeyField: "time_key",
    timeFormat: "%Y%m%d%H%M",
    processData: processKpData,
    startDate: startDate ? startDate.toISOString() : null,
    endDate: endDate ? endDate.toISOString() : null,
    drag:handleDrag,
    zoomTransform,
    onZoom: handleZoom,
    bindGraphGesture,
    dragShift,
    onHover: handleHover
  }), [
    setKpData,
    KpData,
    calculateYDomain,
    processKpData,
    startDate,
    endDate,
    handleDrag,
    zoomTransform,
    handleZoom,
    bindGraphGesture,
    dragShift,
    handleHover
  ]);

// Memoize props for the Bz Solar Wind Graph
const XrayGraphProps = useMemo(() => ({
  route: "/api/data/Xray",
  setData: setXrayData,
  data: XrayData,
  title: "GOES XRay Flux",
  xAxisLabel: "Date",
  yAxisLabel: "watts/m^2",
  yDomain: calculateYDomain(1e-8, 1e-2),
  yValueAccessor: (d) => d.comp_flux,
  lineColor: "steelblue",
  timeKeyField: "time_key",
  timeFormat: "%Y%m%d%H%M",
  processData: processXrayData,
  startDate: startDate ? startDate.toISOString() : null,
  endDate: endDate ? endDate.toISOString() : null,
  drag:handleDrag,
  zoomTransform,
  onZoom: handleZoom,
  bindGraphGesture,
  dragShift,
  onHover: handleHover
}), [
  setXrayData,
  XrayData,
  calculateYDomain,
  processXrayData,
  startDate,
  endDate,
  handleDrag,
  zoomTransform,
  handleZoom,
  bindGraphGesture,
  dragShift,
  handleHover
]);

  // Configuration for multiple lines in Solar Wind graph
  const GNNProtLines = useMemo(() => [
    {
      key: 'P10',
      color: 'orange',
      label: 'P10',
      valueAccessor: (d) => d.P10
    },
    {
      key: 'P8A',
      color: 'red',
      label: 'P8A',
      valueAccessor: (d) => d.P8
    },
    {
      key: 'P5',
      color: 'green',
      label: 'P5',
      valueAccessor: (d) => d.P5
    },
    {
      key: 'P4',
      color: 'blue',
      label: 'P4',
      valueAccessor: (d) => d.P4
    }
  ], []);

  const XrayProtLines = useMemo(() => [
    {
      key: 'Xray Flux',
      color: 'steelblue',
      label: 'Xray Flux',
      valueAccessor: (d) => d.comp_flux
    }
  ], []);

  const dstProtLines = useMemo(() => [
    {
      key: 'Dst',
      color: 'steelblue',
      label: 'Dst',
      valueAccessor: (d) => d.dst_value
    },
    {
      key: 'forecast Dst',
      color: 'grey',
      label: 'Forecast Dst',
      valueAccessor: (d) => d.Dst
    }
  ], []);


  return (
    <div className="App">
      <div className="container">
        {/* Sidebar */}
        <div className={`sidebar-column${isSidebarOpen ? '' : ' sidebar-collapsed'}`}>
          {!isSidebarOpen && (
            <>
              <button
                type="button"
                className="sidebar-toggle"
                onClick={() => setIsSidebarOpen(true)}
                aria-expanded={false}
                aria-label="Show menu"
              >
                Show Menu
              </button>
              <a
                className="sidebar-brand-vertical"
                href="https://spacewx.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Space Environment Technologies"
              >
                <img
                  src="https://spacewx.com/wp-content/uploads/2022/08/logo.png"
                  alt="Space Environment Technologies"
                />
              </a>
            </>
          )}

          {isSidebarOpen && (
            <div className="date-picker-container">
              <Header />
              <button
                type="button"
                className="sidebar-toggle"
                onClick={() => setIsSidebarOpen(false)}
                aria-expanded={true}
                aria-label="Hide menu"
              >
                Hide Menu
              </button>
              <CustomDatePicker
              startDate={startDate} 
              endDate={endDate}
                setStartDate={(date) => {
                  console.log("Start Date Selected:", date);
                  setStartDate(date); // Update startDate from DatePicker
                }}
                setEndDate={(date) => {
                  console.log("End Date Selected:", date);
                  setEndDate(date); // Update endDate from DatePicker
                }}
                onSubmit={() => {
                  if (window.matchMedia('(max-width: 800px)').matches) {
                    setIsSidebarOpen(false);
                  }
                }}
              />
              {/* Hover Info Section */}
              <HoverInfo date={hoverInfo.date} time={hoverInfo.time} value={hoverInfo.value} />
               {/* Xray Index Toggle */}
              <Toggel
                label="Show Xray Flux"
                defaultOn={true}
                isOn={isXrayVisible}
                setIsOn={setIsXrayVisible}
              >
              </Toggel>
              
              {/* Bz Index Toggle */}
              <Toggel
                label="Show Solar Wind"
                defaultOn={true}
                isOn={isSolarWindVisible}
                setIsOn={setIsSolarWindVisible}
              >
              </Toggel>

              {/* Goes Proton Index Toggle */}
              <Toggel
                label="Show Proton Flux"
                defaultOn={true}
                isOn={isGNNProtVisible}
                setIsOn={setIsGNNProtVisible}
              >
              </Toggel>
              
              {/* Dst Index Toggle */}
              <Toggel
                label="Show Dst"
                defaultOn={true}
                isOn={isDstVisible}
                setIsOn={setIsDstVisible}
              >
              </Toggel>

              {/* Kp Index Toggle */}
              <Toggel
                label="Show Kp Index"
                defaultOn={true}
                isOn={isKpVisible}
                setIsOn={setIsKpVisible}
              >
              </Toggel>
            </div>
          )}
        </div>

       
        {/* Graph Containers */}
        <div className={`timeline-container${isSidebarOpen ? '' : ' timeline-expanded'}`}>
           {/* Xray Flux Graph */}
           { isXrayVisible && (
            <div className="graph-container" {...bindGraphGesture()} style={{ touchAction: 'none' }}>
             <FetchData {...XrayGraphProps} />
             <MultiLine
               data={XrayData}
               title="GOES XRay Flux"
               xAxisLabel="Date"
               yAxisLabel="watts/m^2"
               xDomain={[startDate, endDate]}
               yDomain={calculateYDomain(1e-8, 1e-2)}
               yScaleType="log"
               classLabels={XRAY_FLARE_CLASS_LEVELS}
               scaleBarLevels={XRAY_R_SCALE_LEVELS}
               lines={XrayProtLines}
               zoomTransform={zoomTransform}
               onZoom={handleZoom}
               dragShift={dragShift}
               onHover={handleHover}
              /> 
            </div> 
          )}

          {/* Solar Wind Bz */}
          { isSolarWindVisible && <GraphContainer {...BzGraphProps} />}
         
          {/* GNN MultiLine Graph */}
          {isGNNProtVisible && (
            <div className="graph-container" {...bindGraphGesture()} style={{ touchAction: 'none' }}>
              <FetchData {...GNNGraphProps} />
              <MultiLine
                data={GNNProtData}
                title="GOES Proton Flux"
                xAxisLabel="Date"
                yAxisLabel="#cm^2/sec"
                yScaleType="log"
                lines={GNNProtLines}
                zoomTransform={zoomTransform}
                onZoom={handleZoom}
                dragShift={dragShift}
                onHover={handleHover}
              />
            </div>
          )}

          {/* Kyoto DST Graph */}
          { 
            isDstVisible && (
            <div className="graph-container" {...bindGraphGesture()} style={{ touchAction: 'none' }}>
              <FetchData {...dstGraphProps} />
              <MultiLine
                data={dstData}
                title="Kyoto Dst"
                xAxisLabel="Date"
                yAxisLabel="nT"
                yDomain={calculateYDomain(-400, 100)}
                guideLines={DST_G_SCALE_LEVELS}
                lines={dstProtLines}
                zoomTransform={zoomTransform}
                onZoom={handleZoom}
                dragShift={dragShift}
                onHover={handleHover}
              />
            </div>
          )
          }

          {/* Kp Index Graph */}
          { isKpVisible && (
            <div className="graph-container" {...bindGraphGesture()} style={{ touchAction: 'none' }}>
              <FetchData {...KpGraphProps} />
              <Bar
                data={KpData}
                title="Kp Index"
                xAxisLabel="Date"
                yAxisLabel="Kp Index"
                yDomain={calculateYDomain(0, 10)}
                guideLines={KP_G_SCALE_LEVELS}
                yValueAccessor={(d) => d.Kp}
                barColor="steelblue"
                zoomTransform={zoomTransform}
                onZoom={handleZoom}
                dragShift={dragShift}
                onHover={handleHover}
              />
            </div>
          )}


          </div>
        </div>
      </div>
  );
}

export default App;